import {
  downloadFileWithReportKey,
  getUnmodifiedKeys,
  loadFileWithReportKey,
  readReport,
} from '@faceup/crypto'
import { useLocalization } from '@faceup/localization'
import { AttachmentList, FormItemsAnswers, ReportStatusTag } from '@faceup/report'
import { Link, useNavigate } from '@faceup/router'
import { ArrowGoBack, AudioPlayer, Card, TextUrlfy } from '@faceup/ui'
import { Button, Col, Icon, Row, Space, Typography, useNotification } from '@faceup/ui-base'
import {
  FormItemType,
  Institution,
  type Language,
  getTranslation as utilsGetTranslation,
} from '@faceup/utils'
import moment from 'moment-timezone'
import { useContext, useEffect, useMemo, useState } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../../__generated__'
import EmailNotificationsCheckbox from '../../Components/EmailNotificationsCheckbox'
import { LayoutContext } from '../../Contexts/LayoutContext'
import { ReportFormContext } from '../../Contexts/ReportFormContext'
import { useSavedReportsContext } from '../../hooks/useSavedReportsContext'
import { HideMedia } from '../../mq'
import { reportStatusMessages, sharedMessages } from '../../Shared/translations'
import { defineMessages, FormattedMessage, useIntl } from '../../TypedIntl'
import useAuth from '../../utils/auth'
import useMobileFileDownload from '../../utils/useMobileFileDownload'
import useReportAuth from '../../utils/useReportAuth'
import CheckReportIcon from './assets/check-report-icon.svg?react'
import { ReportRow } from './components'

const { Title } = Typography

const fragments = {
  CompanyReportInfo_case: graphql(`
    fragment CompanyReportInfo_case on CaseInterface {
      id
      ... on CompanyReport {
        translation {
          id
          body
          bodyNonce
        }
        category {
          id
          name
        }
      }
      createdAt
      lastChangeDate
      status {
        id
        groupName
        color
      }

      isDeleted
      tag
      organizationalUnit {
        id
        type
      }

      encryptionKey

      attachments {
        id
        name
        url
        mimetype
      }

      answers {
        id
        formItem {
          id
          hasHint
          type
          hintTranslations {
            language
            translation
          }
          labelTranslations {
            language
            translation
          }
        }
      }

      channel {
        id
        isDefault
        defaultLanguage
        sourceType
        config {
          ... on FormReportSourceConfiguration {
            id
            emailNotificationEnabled
            identificationFormItems {
              id
              type
              labelTranslations {
                language
                translation
              }
            }
          }
        }
      }

      ...FormItemsAnswers_case
    }
  `),
  CompanyReportInfo_viewer: graphql(`
    fragment CompanyReportInfo_viewer on CompanyVictim {
      email
      isEmailVerified
      ...EmailNotificationsCheckbox_viewer
    }
  `),
}

const messages = defineMessages({
  report: 'FollowUp.report.detail.title',
  organization: 'Shared.report.organization',
  school: 'Shared.report.school',
  lastUpdated: 'FollowUp.report.lastUpdated',
  classRoom: 'Shared.report.classRoom',
  victimName: 'Shared.report.victimName',
  category: 'Shared.report.category',
  moreInfo: 'Shared.report.moreInfo',
  attachments: 'Shared.report.attachments',
  anonymous: 'Shared.chat.anonymous',
  checkAnotherReport: 'FollowUp.report.checkAnotherReport',
  notification: 'FollowUp.report.notification',
  categoryLabel: 'Shared.report.category',
  institutionLabel: 'Shared.report.organization',
  moreInfoLabel: 'Shared.report.moreInfo',
  moreInfoDetailSchool: 'Shared.CreateReportForm.school.moreInfoDetail',
  moreInfoDetailCompany: 'Shared.CreateReportForm.company.moreInfoDetail',
  created: 'Shared.report.created',
})

type Props = {
  loading: boolean
  case: FragmentType<typeof fragments.CompanyReportInfo_case>
  viewer: FragmentType<typeof fragments.CompanyReportInfo_viewer>
}

const ReportInfo = ({ loading, case: _case, viewer: _viewer }: Props) => {
  const aCase = getFragmentData(fragments.CompanyReportInfo_case, _case)
  const viewer = getFragmentData(fragments.CompanyReportInfo_viewer, _viewer)
  const { formatMessage } = useIntl()
  const { client } = useContext(LayoutContext)
  const { setIsAnonymous } = useContext(ReportFormContext)
  const { language } = useLocalization()
  const { logout } = useReportAuth()
  const fileDownloadHandler = useMobileFileDownload()
  const navigate = useNavigate()
  const [moreInfo, setMoreInfo] = useState('')
  const [victimName, setVictimName] = useState('')
  const { getJwt } = useAuth()
  const { addReport } = useSavedReportsContext()
  const isApp = client === 'app'
  const notification = useNotification()

  const jwt = useMemo(() => getJwt(), [getJwt])

  const report = aCase.__typename === 'CompanyReport' ? aCase : null

  // biome-ignore lint/correctness/useExhaustiveDependencies: AddReport causes infinite loop, notification.error is stable
  useEffect(() => {
    const decryptReport = async () => {
      if (!report) {
        return
      }

      const body = report.translation?.body
      const bodyNonce = report.translation?.bodyNonce

      const payload = await readReport(body ?? '', bodyNonce ?? '', report.encryptionKey ?? '')

      if (payload.isErr()) {
        console.error(payload)
        notification.error({
          message: formatMessage(sharedMessages.encryptionError),
          description: payload.error.message,
        })
        return
      }

      const { moreInfo: decryptedMoreInfo, victimName: decryptedVictimName } = payload.value

      setMoreInfo(decryptedMoreInfo ?? '')
      setVictimName(decryptedVictimName ?? '')
      setIsAnonymous(!decryptedVictimName)

      const { privateKey, publicKey } = getUnmodifiedKeys()
      if (report && jwt && isApp && privateKey && publicKey) {
        addReport({
          keys: {
            jwt,
            publicKey,
            privateKey,
          },
          id: report.tag,
          createdAt: report.createdAt,
          category: report.category.name,
          content: decryptedMoreInfo ?? '',
          status: {
            name: formatMessage(reportStatusMessages[report.status.groupName]),
            color: report.status.color,
          },
        })
      }
    }

    decryptReport()
    // `addReport` creates infinite loop
  }, [formatMessage, report, setIsAnonymous, jwt, isApp])

  const channel = aCase.channel
  const isSchoolDefaultForm =
    aCase.organizationalUnit?.type === Institution.School && channel.isDefault

  const getTranslation = <D extends Language>(
    translations: { language: D; translation: string }[] | undefined
  ): string => utilsGetTranslation(translations ?? [], language, channel.defaultLanguage)

  const currentStatus = aCase.status

  const formItemCategories = aCase.answers?.find(
    answer => answer.formItem.type === FormItemType.Category
  )?.formItem
  const formItemMoreInformation = aCase.answers?.find(
    answer => answer.formItem.type === FormItemType.MoreInformation
  )?.formItem

  const formItemClassroom = aCase.answers?.find(
    answer => answer.formItem.type === FormItemType.Classroom
  )?.formItem

  const config =
    channel.config?.__typename === 'FormReportSourceConfiguration' ? channel.config : null
  const formItemSenderName = config?.identificationFormItems.find(
    formItem => formItem.type === FormItemType.SenderName
  )
  const senderNameLabel = getTranslation(formItemSenderName?.labelTranslations) || (
    <FormattedMessage {...sharedMessages.globalNameLabel} />
  )
  const categoriesLabel = getTranslation(formItemCategories?.labelTranslations) || (
    <FormattedMessage {...messages.categoryLabel} />
  )
  const moreInformationLabel = getTranslation(formItemMoreInformation?.labelTranslations) || (
    <FormattedMessage {...messages.moreInfoLabel} />
  )
  const classRoomLabel = getTranslation(formItemClassroom?.labelTranslations) || (
    <FormattedMessage {...messages.classRoom} />
  )

  const groupedAttachments = Object.groupBy(aCase.attachments ?? [], attachment =>
    attachment.mimetype.startsWith('audio/') ? 'audio' : 'other'
  )

  return (
    <Card loading={loading} className='p-6'>
      <Row gutter={24} className='mb-4' justify='space-between'>
        <Col style={{ display: 'flex', alignItems: 'center' }}>
          <Space size={16} wrap>
            <Title level={2} className='inline-block'>
              <Space wrap>
                {isApp && (
                  <Link to={routes => routes.checkReport()}>
                    <ArrowGoBack />
                  </Link>
                )}
                {formatMessage(messages.report)}
                <span className='sensitive'>{`#${aCase.tag}`}</span>
              </Space>
            </Title>
            <div data-test='report-info-status'>
              <ReportStatusTag
                name={formatMessage(reportStatusMessages[currentStatus.groupName])}
                color={currentStatus.color}
              />
            </div>
          </Space>
        </Col>
        <HideMedia hideWhen='lgDown'>
          <Col>
            <Button
              onClick={() => {
                logout()
                navigate(routes => routes.checkReport(), { replace: true })
              }}
              type='text'
            >
              <Space size={12}>
                <Icon style={{ fontSize: 19, display: 'flex' }} component={CheckReportIcon} />
                <FormattedMessage {...messages.checkAnotherReport} />
              </Space>
            </Button>
          </Col>
        </HideMedia>
      </Row>
      <Row
        gutter={24}
        style={{ borderRadius: 6, background: '#f0f2f4', padding: '16px 2px', marginBottom: 16 }}
      >
        <Col>
          <ReportRow label={<FormattedMessage {...messages.created} />}>
            {moment(aCase.createdAt).format('LLL')}
          </ReportRow>
        </Col>
        <Col>
          <ReportRow label={<FormattedMessage {...messages.lastUpdated} />}>
            {moment(aCase.lastChangeDate).format('LLL')}
          </ReportRow>
        </Col>
      </Row>
      <Row gutter={24} className='mb-4'>
        <Col span={24}>
          <ReportRow
            label={
              isSchoolDefaultForm ? <FormattedMessage {...messages.victimName} /> : senderNameLabel
            }
            dataTest={!isSchoolDefaultForm ? 'report-info-victim' : undefined}
          >
            {victimName || <FormattedMessage {...messages.anonymous} />}
          </ReportRow>
        </Col>
      </Row>
      {report && (
        <Row gutter={24} className='mb-4'>
          <Col span={24}>
            <ReportRow label={categoriesLabel} dataTest='report-info-category'>
              {report.category.name}
            </ReportRow>
          </Col>
        </Row>
      )}

      {aCase && (
        <FormItemsAnswers
          companyReport={aCase}
          renderFormItemAnswer={({ label, value, type, attachments }) => {
            if ([FormItemType.Category, FormItemType.OrganizationalUnit].includes(type)) {
              return null
            }
            if (type === FormItemType.MoreInformation && (moreInfo || attachments)) {
              return (
                <Row gutter={24} className='mb-4'>
                  <Col span={24}>
                    <ReportRow label={moreInformationLabel} dataTest='report-info-more-info'>
                      <TextUrlfy text={moreInfo} />
                      {attachments && attachments.length > 0 && (
                        <AttachmentList
                          attachments={attachments}
                          config={{
                            onDownload: async attachment =>
                              !(await downloadFileWithReportKey(
                                attachment,
                                aCase.encryptionKey ?? '',
                                client === 'app' ? fileDownloadHandler : undefined
                              )),
                          }}
                        />
                      )}
                    </ReportRow>
                  </Col>
                </Row>
              )
            }
            if (type === FormItemType.File) {
              return (
                <Row gutter={24} className='mb-4'>
                  <Col span={24}>
                    <ReportRow label={label} dataTest={`report-info-answer-${type}`}>
                      <AttachmentList
                        attachments={attachments ?? []}
                        config={{
                          onDownload: async attachment =>
                            !(await downloadFileWithReportKey(
                              attachment,
                              aCase.encryptionKey ?? '',
                              client === 'app' ? fileDownloadHandler : undefined
                            )),
                        }}
                      />
                    </ReportRow>
                  </Col>
                </Row>
              )
            }
            return (
              <Row gutter={24} className='mb-4'>
                <Col span={24}>
                  <ReportRow
                    label={type === FormItemType.Classroom ? classRoomLabel : label}
                    dataTest={`report-info-answer-${type}`}
                  >
                    {value}
                  </ReportRow>
                </Col>
              </Row>
            )
          }}
        />
      )}

      {Boolean(aCase.attachments?.length) && (
        <Row gutter={24} className='mb-4'>
          <Col span={24}>
            <ReportRow label={<FormattedMessage {...messages.attachments} />}>
              {groupedAttachments.audio?.map(attachment => (
                <div key={attachment.id} className='mb-2'>
                  <AudioPlayer
                    label={attachment.name}
                    onPlay={async () => {
                      const result = await loadFileWithReportKey(
                        attachment,
                        aCase.encryptionKey ?? ''
                      )
                      if (result.isErr()) {
                        throw new Error('Failed to load audio file')
                      }
                      return URL.createObjectURL(result.value)
                    }}
                    onDownload={async () => {
                      await downloadFileWithReportKey(
                        attachment,
                        aCase.encryptionKey ?? '',
                        client === 'app' ? fileDownloadHandler : undefined
                      )
                    }}
                  />
                </div>
              ))}
              {Boolean(groupedAttachments.other?.length) && (
                <AttachmentList
                  attachments={groupedAttachments.other ?? []}
                  config={{
                    onDownload: async attachment =>
                      !(await downloadFileWithReportKey(
                        attachment,
                        aCase.encryptionKey ?? '',
                        client === 'app' ? fileDownloadHandler : undefined
                      )),
                    renderFileName: (_children, attachment) => <TextUrlfy text={attachment.name} />,
                  }}
                />
              )}
            </ReportRow>
          </Col>
        </Row>
      )}
      {((channel.config.__typename === 'FormReportSourceConfiguration' &&
        channel.config.emailNotificationEnabled) ||
        viewer.email) && (
        <Row gutter={24}>
          <Col span={24}>
            <EmailNotificationsCheckbox viewer={viewer} />
          </Col>
        </Row>
      )}
    </Card>
  )
}

export default ReportInfo
